import { normalizeCpfCnpj, validarCPF, validarCNPJ } from './utils';
import { supabase } from './supabase';
import { nomesParecemMesmaPessoa, conflitoBarraEmissao, mesmoNomeNoBanco, boletoVigenteDoDocumento } from './nomePagador';
import { nomeOficialDocumento } from './nomeOficialDocumento';

/**
 * Detecta o erro de "empresa certa, filial errada" no pagador do boleto.
 *
 * Contexto (jul/2026): a UC de Marzagão estava cadastrada com o CNPJ da unidade
 * de Água Limpa no campo do cliente/pagador. Como o boleto usa o pagador da UC e
 * só cai no titular como fallback, saiu cobrança no CNPJ da unidade errada — e
 * ninguém percebeu até o cliente reclamar. O mesmo estava acontecendo com o
 * Sicoob Ouvidor (matriz 0001-92 no lugar da filial 0010-83).
 *
 * Cliente != titular é NORMAL no modelo (o pagador pode legitimamente ser outra
 * pessoa). O que quase nunca é intencional é os dois serem CNPJ da MESMA empresa
 * em filiais diferentes: mesma raiz de 8 dígitos, sufixo de filial distinto.
 * Numa varredura da base inteira, 236 UCs tinham cliente != titular mas só 8
 * caíam nesse padrão — e as que dava para conferir estavam erradas.
 *
 * Por isso o alerta é restrito a esse recorte: barulho quase zero, e pega
 * exatamente o caso que gerou retrabalho.
 */

const digitos = (v?: string | null): string => (v ?? '').replace(/\D/g, '');

/** True quando os dois documentos são CNPJ da mesma empresa em filiais diferentes. */
export const mesmaEmpresaFilialDiferente = (a?: string | null, b?: string | null): boolean => {
    const x = digitos(a);
    const y = digitos(b);
    if (x.length !== 14 || y.length !== 14) return false; // só CNPJ
    if (x === y) return false;
    return x.slice(0, 8) === y.slice(0, 8);
};

/**
 * Mensagem de alerta para o pagador do boleto, ou null quando está tudo certo.
 * Não bloqueia a emissão — a decisão continua de quem emite.
 */
export const alertaPagadorFilialDivergente = (
    cpfCnpjPagador?: string | null,
    cpfCnpjTitular?: string | null,
): string | null => {
    if (!mesmaEmpresaFilialDiferente(cpfCnpjPagador, cpfCnpjTitular)) return null;
    const p = digitos(cpfCnpjPagador);
    const t = digitos(cpfCnpjTitular);
    return `O CNPJ do pagador (…${p.slice(8, 12)}-${p.slice(12)}) é de outra filial da mesma empresa que o titular da fatura (…${t.slice(8, 12)}-${t.slice(12)}). Confirme qual unidade deve receber a cobrança antes de emitir.`;
};

/* -------------------------------------------------------------------------- */
/* Pagador do boleto: nome + documento, sempre em par                          */
/* -------------------------------------------------------------------------- */

/**
 * O caso AFCOM/ARE (chamado 11/08/2026) é o irmão mais grave do de cima: em vez
 * de filial errada, empresa errada. Nasceu de compor o pagador com nome de uma
 * fonte e documento de outra — o cadastro da UC da AFCOM tinha nome mas o
 * `cpf_cnpj_cliente` estava vazio, então a cadeia `ucData.x || prev.x || fatura.x`
 * pegava o nome do cadastro e o CNPJ de um cálculo anterior, que era o da
 * Associação Rodoviária Esportiva. Como o Sicoob mantém UM cadastro de pagador
 * por CPF/CNPJ, cada emissão reescreveu o nome daquele CNPJ e os 23 boletos da
 * ARE passaram a sair no nome da AFCOM.
 *
 * Regra: a primeira fonte que tem NOME define os dois campos. Se essa fonte não
 * tem documento, o documento fica vazio (e a emissão barra pedindo o cadastro) —
 * nunca herda o de outro lugar.
 */

export interface FontePagador {
    /** De onde veio, para explicar ao operador na tela. */
    origem: string;
    nome?: string | null;
    documento?: string | null;
}

export interface Pagador {
    nome: string;
    documento: string;
    origem: string;
}

const PAGADOR_VAZIO: Pagador = { nome: '', documento: '', origem: 'nenhuma' };

/** Escolhe o pagador pela primeira fonte com nome preenchido, levando o documento DELA junto. */
export function escolherPagador(fontes: FontePagador[]): Pagador {
    for (const fonte of fontes) {
        const nome = (fonte.nome || '').trim();
        if (!nome) continue;
        return { nome, documento: normalizeCpfCnpj(fonte.documento || ''), origem: fonte.origem };
    }
    return PAGADOR_VAZIO;
}

export type SituacaoDocumento = 'vazio' | 'cpf' | 'cnpj' | 'invalido';

export function situacaoDocumento(documento: string | null | undefined): SituacaoDocumento {
    const doc = normalizeCpfCnpj(documento || '');
    if (!doc) return 'vazio';
    if (doc.length === 11) return validarCPF(doc) ? 'cpf' : 'invalido';
    if (doc.length === 14) return validarCNPJ(doc) ? 'cnpj' : 'invalido';
    return 'invalido';
}

export function documentoValido(documento: string | null | undefined): boolean {
    const s = situacaoDocumento(documento);
    return s === 'cpf' || s === 'cnpj';
}

export interface ConflitoPagador {
    /** Nome com que esse CPF/CNPJ já foi registrado no banco. */
    nomeRegistrado: string;
    faturaId: number;
    numCobranca: string | null;
    /**
     * Os nomes são de pessoas diferentes (não só grafia). Aí a emissão é barrada:
     * só admin libera (#6155 — Thiago x Vera).
     */
    pessoaDiferente: boolean;
    /**
     * Nome do dono do documento na Receita, quando foi consultado (só nos casos em
     * que os nomes pareciam de pessoas diferentes — a consulta é paga). #6160
     */
    nomeOficial?: string | null;
}

const chaveNome = (v: string) =>
    v.normalize('NFD').replace(/\p{Diacritic}/gu, '').toUpperCase().replace(/\s+/g, ' ').trim();

/**
 * True quando nome+documento são idênticos ao titular da fatura na Equatorial.
 *
 * O boleto Vigor tem que sair no nome de quem ASSINOU o termo com a Vigor (o
 * assinante/associado), nunca no titular da conta de luz na distribuidora — são
 * papéis deliberadamente distintos no negócio: existem assinantes ativos cuja
 * fatura de energia continua no nome de outra pessoa (ex: quem não conseguiu
 * trocar a titularidade na distribuidora), e o titular "só está no sistema para
 * fins de registro" (caso Darcy/Bruno, UC 69401701217, ago/2026: cobrança saiu no
 * nome da titular da conta de luz em vez do assinante). Usada para não confiar
 * cegamente em "cálculo anterior da fatura": esse campo pode ter sido preenchido,
 * antes desta correção, com o titular da Equatorial — e ficaria "escondido" sob
 * esse rótulo em qualquer fatura calculada antes do fix.
 */
export const pareceTitularEquatorial = (
    nome?: string | null,
    documento?: string | null,
    titularNome?: string | null,
    titularDocumento?: string | null,
): boolean => {
    const nome1 = chaveNome(nome || '');
    const nome2 = chaveNome(titularNome || '');
    if (!nome1 || nome1 !== nome2) return false;
    const doc1 = normalizeCpfCnpj(documento || '');
    const doc2 = normalizeCpfCnpj(titularDocumento || '');
    return !!doc1 && doc1 === doc2;
};

/**
 * O Sicoob guarda um cadastro de pagador por CPF/CNPJ: emitir com o mesmo
 * documento sob outro nome reescreve o pagador de TODOS os boletos daquele
 * documento, inclusive os de outro cliente. Antes de emitir, confere se o nome
 * que o banco tem hoje para o documento — o do último boleto — é outro.
 *
 * Até 13/10/2026 comparava com QUALQUER boleto antigo do documento: uma troca de
 * nome já confirmada voltava a avisar em toda emissão seguinte, com o banco já no
 * nome certo. Foram 113 dos 167 avisos dos 30 dias anteriores (um CPF com 12
 * boletos seguidos no mesmo nome pedia ressalva por causa de um boleto de abril).
 *
 * Filtra por igualdade exata porque a trigger `trigger_forcar_cpf_numeros_boleto`
 * grava `cpf_cnpj_cliente_boleto` sempre só com dígitos.
 */
export async function conflitoDePagador(
    documento: string | null | undefined,
    nome: string | null | undefined,
    faturaIdAtual?: string | number | null
): Promise<ConflitoPagador | null> {
    const doc = normalizeCpfCnpj(documento || '');
    const nomeAtual = chaveNome(nome || '');
    if (!doc || !nomeAtual) return null;

    // Basta o último boleto e, no máximo, a própria fatura. Boleto de teste não
    // passa pelo Sicoob e não mexe no nome de lá.
    const { data, error } = await supabase
        .from('faturas')
        .select('id, nome_cliente_boleto, num_cobranca_boleto, emitido_em')
        .eq('cpf_cnpj_cliente_boleto', doc)
        .eq('ambiente', 'producao')
        .not('num_cobranca_boleto', 'is', null)
        .not('nome_cliente_boleto', 'is', null)
        .order('emitido_em', { ascending: false, nullsFirst: false })
        .order('id', { ascending: false })
        .limit(2);
    if (error || !data) return null;

    const vigente = boletoVigenteDoDocumento(data as any[], faturaIdAtual);
    if (!vigente || mesmoNomeNoBanco(vigente.nome_cliente_boleto, nome)) return null;

    const conflito: ConflitoPagador = {
        nomeRegistrado: vigente.nome_cliente_boleto,
        faturaId: vigente.id,
        numCobranca: vigente.num_cobranca_boleto,
        pessoaDiferente: !nomesParecemMesmaPessoa(vigente.nome_cliente_boleto, nome),
    };
    if (conflito.pessoaDiferente) {
        // Pela comparação de nomes são pessoas diferentes: antes de barrar,
        // pergunta à Receita de quem é o documento (#6160).
        const oficial = await nomeOficialDocumento(doc);
        conflito.nomeOficial = oficial;
        conflito.pessoaDiferente = conflitoBarraEmissao(nome, vigente.nome_cliente_boleto, oficial);
    }
    return conflito;
}
